import { ReactNode, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { clsx as c } from 'clsx'

import styles from './EnterpriseSettings.module.scss'

import { isApiError } from '@/api'
import { updateEnterpriseGates, updateEnterpriseSettings, updatePreserveContractedSeats } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { Switch } from '@/components/Switch'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { enterpriseDetailsQueryKey, useEnterpriseDetails } from '@/hooks/useEnterpriseDetails'
import { mdmProviderLabel, mdmUpdateIntentLabel } from '@/pages/app/Settings/mdmLabels'
import { selectIsInternalAdmin, selectIsInternalSuperAdmin } from '@/store/features/user/userSlice'
import { ContextAwarenessPolicy, EnterpriseGates, EnterpriseSettings as EnterpriseSettingsType } from '@/types'

interface EnterpriseSettingsProps {
  enterpriseId: string
}

interface SettingRowProps {
  label: string
  hint: string
  control: ReactNode
  enabled?: boolean
  hidden?: boolean
}

const SettingRow = ({ label, hint, control, enabled, hidden }: SettingRowProps) =>
  hidden ? null : (
    <div className={styles.row}>
      <span className={styles.key}>
        {label}
        <span className={styles.hint}>{hint}</span>
      </span>
      <span className={styles.value}>
        {enabled !== undefined && (
          <span className={c(styles.status, enabled && styles.statusEnabled)}>{enabled ? 'Enabled' : 'Disabled'}</span>
        )}
        {control}
      </span>
    </div>
  )

const EnterpriseSettings = ({ enterpriseId }: EnterpriseSettingsProps) => {
  const queryClient = useQueryClient()
  const { data: enterpriseDetails, isLoading } = useEnterpriseDetails(enterpriseId)
  // Same Growth/Enterprise check the backend applies to plan-gated features (`is_growth_or_enterprise_plan`).
  const planIneligible = enterpriseDetails?.data?.dictionary_snippets_plan_eligible === false
  const isSuperAdmin = useSelector(selectIsInternalSuperAdmin)
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const { updateToast } = useToast()

  const [showDisableTrialDialog, setShowDisableTrialDialog] = useState(false)

  const zdrLocked = enterpriseDetails?.data?.zdr_locked ?? false
  const canEditZdr = isInternalAdmin && !zdrLocked
  const zdrTooltip = !isInternalAdmin
    ? 'Only Wispr admins can update this'
    : zdrLocked && isSuperAdmin
      ? 'Unlock ZDR first to change Privacy Mode'
      : zdrLocked
        ? 'ZDR is locked. Only superadmins can unlock it.'
        : undefined

  const invalidate = () => queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) })

  const settingsMutation = useMutation({
    mutationFn: ({ settings }: { settings: Partial<EnterpriseSettingsType> }) =>
      updateEnterpriseSettings(enterpriseId, settings),
    onSuccess: invalidate,
    onError: (error) => {
      const detail = isApiError(error) ? error.detail : undefined
      updateToast(detail || 'Failed to update enterprise settings', 'error')
    },
  })

  const gatesMutation = useMutation({
    mutationFn: ({ gates }: { gates: Partial<EnterpriseGates> }) => updateEnterpriseGates(enterpriseId, gates),
    onSuccess: invalidate,
    onError: (error) => {
      const detail = isApiError(error) ? error.detail : undefined
      updateToast(detail || 'Failed to update enterprise gates', 'error')
    },
  })

  const preserveSeatsMutation = useMutation({
    mutationFn: (value: boolean) => updatePreserveContractedSeats(enterpriseId, value),
    onSuccess: invalidate,
    onError: () => updateToast('Failed to update preserve contracted seats', 'error'),
  })

  const settingsPending = settingsMutation.isPending
  const gatesPending = gatesMutation.isPending
  const preservePending = preserveSeatsMutation.isPending

  const disableTeamTrial = enterpriseDetails?.data?.disable_team_trial ?? false
  const contextAwarenessDisabled =
    (enterpriseDetails?.data?.context_awareness ?? ContextAwarenessPolicy.Available) === ContextAwarenessPolicy.Disabled
  const mdmProviders = enterpriseDetails?.data?.mdm_providers ?? []
  const mdmUpdateIntent = enterpriseDetails?.data?.mdm_update_intent ?? null

  const confirmEnableDisableTrial = () => {
    setShowDisableTrialDialog(false)
    settingsMutation.mutate({ settings: { disableTeamTrial: true } })
  }

  return (
    <div className={styles.settingsSection}>
      <div className={styles.header}>
        <span className={styles.label}>Settings</span>
      </div>
      {planIneligible && (
        <span className={styles.hint}>
          This organization is not on the Growth or Enterprise plan: Dictionary &amp; Snippets, and IP allowlist or
          audit log streaming options that are off, are hidden.
        </span>
      )}

      <div className={styles.group}>
        <div className={styles.groupHeader}>Privacy &amp; Compliance</div>
        <SettingRow
          label="Granular data controls UI"
          enabled={enterpriseDetails?.data?.granular_data_controls_ui_visible ?? true}
          hint="Shows separate Privacy Mode and Private Cloud Sync toggles in the customer admin portal"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={enterpriseDetails?.data?.granular_data_controls_ui_visible ?? true}
                onInputChange={(checked) => gatesMutation.mutate({ gates: { granularDataControlsUiVisible: checked } })}
                disabled={!isInternalAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Privacy Mode (Zero Data Retention)"
          enabled={enterpriseDetails?.data?.zdr ?? false}
          hint="Enables Privacy Mode for this enterprise"
          control={
            <Tooltip text={zdrTooltip} placement="top" hideArrow>
              <Switch
                checked={enterpriseDetails?.data?.zdr ?? false}
                onInputChange={(checked) => settingsMutation.mutate({ settings: { zdr: checked } })}
                disabled={!canEditZdr || settingsPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Lock Zero Data Retention"
          enabled={zdrLocked}
          hint="Prevents enterprise members and admins (except superadmins) from toggling Privacy Mode"
          control={
            <Tooltip text={!isSuperAdmin ? 'Only superadmins can update this' : undefined} placement="top" hideArrow>
              <Switch
                checked={enterpriseDetails?.data?.zdr_locked ?? false}
                onInputChange={(checked) => gatesMutation.mutate({ gates: { zdrLocked: checked } })}
                disabled={!isSuperAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="IP allowlist UI"
          hidden={planIneligible && !enterpriseDetails?.data?.ip_allowlist_ui_visible}
          enabled={enterpriseDetails?.data?.ip_allowlist_ui_visible ?? false}
          hint="Shows the IP allowlist card in the customer admin portal"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={enterpriseDetails?.data?.ip_allowlist_ui_visible ?? false}
                onInputChange={(checked) => gatesMutation.mutate({ gates: { ipAllowlistUiVisible: checked } })}
                disabled={!isInternalAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Lock IP allowlist"
          hidden={planIneligible && !enterpriseDetails?.data?.ip_allowlist_locked}
          enabled={enterpriseDetails?.data?.ip_allowlist_locked ?? false}
          hint="Prevents enterprise admins (except superadmins) from editing the IP allowlist"
          control={
            <Tooltip text={!isSuperAdmin ? 'Only superadmins can update this' : undefined} placement="top" hideArrow>
              <Switch
                checked={enterpriseDetails?.data?.ip_allowlist_locked ?? false}
                onInputChange={(checked) => gatesMutation.mutate({ gates: { ipAllowlistLocked: checked } })}
                disabled={!isSuperAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Audit log streaming"
          hidden={planIneligible && !enterpriseDetails?.data?.audit_log_streaming_ui_visible}
          enabled={enterpriseDetails?.data?.audit_log_streaming_ui_visible ?? false}
          hint="Enables SIEM audit log streaming (UI and API). The UI shows on the Enterprise plan only"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                aria-label="Audit log streaming"
                checked={enterpriseDetails?.data?.audit_log_streaming_ui_visible ?? false}
                onInputChange={(checked) => gatesMutation.mutate({ gates: { auditLogStreamingUiVisible: checked } })}
                disabled={!isInternalAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Allow organization to disable Dictionary & Snippets"
          hidden={planIneligible}
          enabled={enterpriseDetails?.data?.dictionary_snippets_kill_switch_ui_visible ?? true}
          hint="Controls whether this organization's admins are allowed to choose to turn Dictionary & Snippets off (Settings → Organization). Turning this on does NOT disable Dictionary or Snippets; it only makes that option available to the organization. Turning it off hides the option and any Dictionary/Snippets switch the organization already set stops applying. Growth and Enterprise plans only."
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                aria-label="Allow organization to disable Dictionary & Snippets"
                checked={enterpriseDetails?.data?.dictionary_snippets_kill_switch_ui_visible ?? true}
                onInputChange={(checked) =>
                  gatesMutation.mutate({ gates: { dictionarySnippetsKillSwitchUiVisible: checked } })
                }
                disabled={!isInternalAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Disable Context Awareness"
          enabled={contextAwarenessDisabled}
          hint="Forces context awareness off for all enterprise users"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={contextAwarenessDisabled}
                onInputChange={(checked) =>
                  settingsMutation.mutate({
                    settings: {
                      contextAwareness: checked ? ContextAwarenessPolicy.Disabled : ContextAwarenessPolicy.Available,
                    },
                  })
                }
                disabled={!isInternalAdmin || settingsPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Notetaker"
          enabled={enterpriseDetails?.data?.allow_notetaker ?? true}
          hint="Blocks Notetaker org-wide when off; member overrides take precedence, and HIPAA and flags still apply"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={enterpriseDetails?.data?.allow_notetaker ?? true}
                onInputChange={(checked) => settingsMutation.mutate({ settings: { allowNotetaker: checked } })}
                disabled={!isInternalAdmin || settingsPending || isLoading}
              />
            </Tooltip>
          }
        />
      </div>

      <div className={styles.group}>
        <div className={styles.groupHeader}>Billing</div>
        <SettingRow
          label="Automatically add new users with this enterprise's email domain"
          enabled={enterpriseDetails?.data?.auto_invite ?? true}
          hint="When a user signs up with the enterprise's domain, they are added to the enterprise automatically"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={enterpriseDetails?.data?.auto_invite ?? true}
                onInputChange={(checked) => {
                  if (!isInternalAdmin) return
                  settingsMutation.mutate({ settings: { autoInvite: checked } })
                }}
                disabled={!isInternalAdmin || settingsPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Preserve contracted seats"
          enabled={enterpriseDetails?.data?.preserve_contracted_seats ?? false}
          hint="Prevents automatic seat reduction when members are removed"
          control={
            <Switch
              checked={enterpriseDetails?.data?.preserve_contracted_seats ?? false}
              onInputChange={(checked) => preserveSeatsMutation.mutate(checked)}
              disabled={preservePending || isLoading}
            />
          }
        />
        <SettingRow
          label="Disable free trial for new members joining this enterprise"
          enabled={disableTeamTrial}
          hint="New members skip the team trial and are billable immediately"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={disableTeamTrial}
                onInputChange={(checked) => {
                  if (!isInternalAdmin) return
                  if (checked && !disableTeamTrial) {
                    setShowDisableTrialDialog(true)
                  } else {
                    settingsMutation.mutate({ settings: { disableTeamTrial: checked } })
                  }
                }}
                disabled={!isInternalAdmin || settingsPending || isLoading}
              />
            </Tooltip>
          }
        />
      </div>

      <div className={styles.group}>
        <div className={styles.groupHeader}>Data Access</div>
        <SettingRow
          label="Hide Leaderboard"
          enabled={enterpriseDetails?.data?.hide_leaderboard ?? false}
          hint="Hides the team leaderboard for non-admin members of this enterprise"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={enterpriseDetails?.data?.hide_leaderboard ?? false}
                onInputChange={(checked) => {
                  if (!isInternalAdmin) return
                  settingsMutation.mutate({ settings: { hideLeaderboard: checked } })
                }}
                disabled={!isInternalAdmin || settingsPending || isLoading}
              />
            </Tooltip>
          }
        />
        <SettingRow
          label="Allow Usage Export"
          enabled={enterpriseDetails?.data?.usage_export_allowed ?? false}
          hint="Allows enterprise admins to export usage insights as CSV"
          control={
            <Switch
              checked={enterpriseDetails?.data?.usage_export_allowed ?? false}
              onInputChange={(checked) => gatesMutation.mutate({ gates: { usageExportAllowed: checked } })}
              disabled={gatesPending || isLoading}
            />
          }
        />
        <SettingRow
          label="Show Last Used At"
          enabled={enterpriseDetails?.data?.show_last_used_at ?? false}
          hint="Shows each member's last-used date in the Usage table and member pages for this enterprise"
          control={
            <Tooltip
              text={!isInternalAdmin ? 'Only Wispr admins can update this' : undefined}
              placement="top"
              hideArrow
            >
              <Switch
                checked={enterpriseDetails?.data?.show_last_used_at ?? false}
                onInputChange={(checked) => gatesMutation.mutate({ gates: { showLastUsedAt: checked } })}
                disabled={!isInternalAdmin || gatesPending || isLoading}
              />
            </Tooltip>
          }
        />
      </div>

      <div className={styles.group}>
        <div className={styles.groupHeader}>MDM</div>
        <SettingRow
          label="MDM providers"
          hint="What the org admin said they use. Read only; the admin sets it in Settings, Organization"
          control={
            <span>{mdmProviders.length > 0 ? mdmProviders.map(mdmProviderLabel).join(', ') : 'Not declared'}</span>
          }
        />
        <SettingRow
          label="MDM update plan"
          hint="How the org admin expects Wispr Flow to be updated on managed devices. Read only"
          control={<span>{mdmUpdateIntent ? mdmUpdateIntentLabel(mdmUpdateIntent) : 'Not declared'}</span>}
        />
      </div>

      <DialogWrapper
        isOpen={showDisableTrialDialog}
        title="Disable free trial for new members?"
        subtitle="Enabling this setting will immediately end the team trial for every current member who is still trialing — they will be billable right away, with no grace period. This cannot be undone."
        close={() => setShowDisableTrialDialog(false)}
      >
        <div className={styles.dialogActions}>
          <Button title="Cancel" type="light" onClick={() => setShowDisableTrialDialog(false)} />
          <Button title="Disable trial" type="destructive" onClick={confirmEnableDisableTrial} />
        </div>
      </DialogWrapper>
    </div>
  )
}

export default EnterpriseSettings
